import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Card from '@mui/material/Card';


import {
  SortingState,
  IntegratedSorting,
  IntegratedFiltering,
  IntegratedPaging,
  EditingState,
  FilteringState,
  PagingState,
} from '@devexpress/dx-react-grid';
import BaseTable from '../../../../components/Table/BaseTable';

import { showLoading, hideLoading } from '../../../../store/app';
import WebAPIClient, {
  errorResponseToastr,
} from '../../../../api/_energytracer'


const intensity_utilities = ["total", "electric", "gas"]
const intensity_types = ["usage", "cost"]
const climate_zones = [
  "1A",
  "1B",
  "2A",
  "2B",
  "3A",
  "3B",
  "3C",
  "4A",
  "4B",
  "4C",
  "5A",
  "5B",
  "5C",
  "6A",
  "6B",
  "7",
  "8",
]

const states = [
  "AL",
  "AK",
  "AS",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  'ME',
  'MD',
  "MA",
  "MI",
  'MN',
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  'OK',
  "OR",
  "PA",
  "RI",
  'SC',
  "SD",
  "TN",
  "TX",
  "UT",
  "VI",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
]


export default function QueryBenchmark() {
  const dispatch = useDispatch();

  const [requestData, setRequestData] = useState([]);

  const [zone, setZone] = useState('5A');
  const [utility, setUtility] = useState('total');
  const [intensity, setIntensity] = useState('usage');
  const [climateZoneCounts, setClimateZoneCounts] = useState({});

  const [stateUS, setStateUS] = useState('OH')
  const [stateData, setStateData] = useState([]);
  const [tableData, setTableData] = useState([]);

  const [tableColumnExtensions] = useState([
    { columnName: 'active', align: 'center' },
  ]);
  const [integratedFilteringColumnExtensions] = useState([
    {
      columnName: 'name',
      predicate: (value, filter) => {
        return value.toLowerCase().includes(filter.value.toLowerCase());
      },
    },
  ]);
  const [integratedSortingColumnExtensions] = useState([
    {
      columnName: 'name',
      compare: (a, b) => {
        return a.toLowerCase() < b.toLowerCase() ? -1 : 1;
      },
    },
  ]);
  const [columns] = useState([
    { title: 'Organization', name: 'name', width: 0.3 },
    { title: 'Average', name: 'average', width: 0.1 },
    { title: 'Max', name: 'max', width: 0.1 },
    { title: 'Min', name: 'min', width: 0.1 },
    { title: 'Building Count', name: 'building_count', width: 0.15 },
    { title: 'Building Area', name: 'building_sqft', width: 0.15 },
    { title: 'Date', name: 'date', width: 0.1 },
    { title: 'Automated', name: 'automated', width: 0.1 },

  ]);


  const fetchData = useCallback(async () => {
    dispatch(showLoading());
    try {
      const data = await new WebAPIClient().GET('/veregy_benchmarking/query_benchmark_statistics');
      setRequestData(data)
    } catch (err) {
      errorResponseToastr(err);
    } finally {
      dispatch(hideLoading());
    }
    dispatch(hideLoading());
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const fetchStateData = useCallback(async (stateUS) => {
    dispatch(showLoading());
    try {
      const data = await new WebAPIClient().GET(`/veregy_benchmarking/state/${stateUS}`);
      setStateData(data)
    } catch (err) {
      errorResponseToastr(err);
    } finally {
      dispatch(hideLoading());
    }
    dispatch(hideLoading());
  }, [dispatch]);

  useEffect(() => {
    setStateData([])
    fetchStateData(stateUS);
  }, [fetchStateData, stateUS]);

  useEffect(() => {
    let climate_zone_counts = {}
    climate_zones.forEach((zone) => {
      climate_zone_counts[zone] = 0;
      if (Object.keys(requestData).includes(zone) && requestData[zone]['total']['usage']) {
        climate_zone_counts[zone] = requestData[zone]['total']['usage']['quality_statistics']['count']
      }
    })
    setClimateZoneCounts(climate_zone_counts)
  }, [requestData])

  useEffect(() => {
    let table_data = [];

    stateData.forEach((org) => {
      let temp_obj = {};
      temp_obj['org_id'] = org.organization_id;
      temp_obj['name'] = org.name;
      temp_obj['building_count'] = org.building_count;
      temp_obj['building_sqft'] = org.building_sqft.toLocaleString();
      let benchmark = org.benchmarks.usage.total
      temp_obj['date'] = benchmark.date;
      temp_obj['average'] = parseFloat(benchmark.average).toFixed(2);
      temp_obj['min'] = parseFloat(benchmark.min).toFixed(2);
      temp_obj['max'] = parseFloat(benchmark.max).toFixed(2);
      temp_obj['automated'] = benchmark.automated ? 'true' : 'false';
      table_data.push(temp_obj)
    })
    setTableData(table_data);
  }, [stateData])



  return <div>
    <div style={{ 'width': '33%', marginLeft: '33%' }}>
      <h3>Benchmark Lookup</h3>
      <form>
        <List disablePadding>
          <ListItem disableGutters>
            <FormControl id='zone' variant='standard' fullWidth>
              <InputLabel id='zone-label'>Climate Zone</InputLabel>
              <Select
                labelId='zone-label'
                name='zone'
                onChange={(e) => {
                  setZone(e.target.value);
                }}
                value={zone}
                MenuProps={{
                  MenuListProps: { disablePadding: true },
                  slotProps: {
                    paper: { sx: { minWidth: 'unset !important' } },
                  },
                }}
                sx={{
                  backgroundColor: 'unset',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}>
                <MenuItem value=''></MenuItem>
                {climate_zones.map((zone) => {
                  return (
                    <MenuItem key={zone} value={zone}>
                      {zone} {`(${Object.keys(climateZoneCounts).includes(zone) ? climateZoneCounts[zone] : 0})`}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </ListItem>
          <ListItem disableGutters>
            <FormControl id='utility' variant='standard' fullWidth>
              <InputLabel id='utility-label'>Utility Type</InputLabel>
              <Select
                labelId='utility-label'
                name='utility'
                onChange={(e) => {
                  setUtility(e.target.value);
                }}
                value={utility}
                MenuProps={{
                  MenuListProps: { disablePadding: true },
                  slotProps: {
                    paper: { sx: { minWidth: 'unset !important' } },
                  },
                }}
                sx={{
                  backgroundColor: 'unset',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}>
                <MenuItem value=''></MenuItem>
                {intensity_utilities.map((util) => {
                  return (
                    <MenuItem key={util} value={util}>
                      {util}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </ListItem>
          <ListItem disableGutters>
            <FormControl id='intensity' variant='standard' fullWidth>
              <InputLabel id='intensity-label'>Intensity Type</InputLabel>
              <Select
                labelId='intensity-label'
                name='intensity'
                onChange={(e) => {
                  setIntensity(e.target.value);
                }}
                value={intensity}
                MenuProps={{
                  MenuListProps: { disablePadding: true },
                  slotProps: {
                    paper: { sx: { minWidth: 'unset !important' } },
                  },
                }}
                sx={{
                  backgroundColor: 'unset',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}>
                <MenuItem value=''></MenuItem>
                {intensity_types.map((i_type) => {
                  return (
                    <MenuItem key={i_type} value={i_type}>
                      {i_type}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </ListItem>
        </List>
      </form>
      <h4>Benchmark Information</h4>
      {(Object.keys(requestData).includes(zone) && Object.keys(requestData[zone]).includes(utility) && Object.keys(requestData[zone][utility]).includes(intensity) && requestData[zone][utility][intensity] !== null) ? (<BenchmarkInfo benchmarkData={requestData[zone][utility][intensity]} />) : (<div>No data available</div>)}

    </div>
    <div>
      <h3>Benchmark Lookup By State</h3>
      <form style={{ width: '200px' }}>
        <List disablePadding>
          <ListItem disableGutters>
            <FormControl id='state' variant='standard' fullWidth>
              <InputLabel id='state-label'>States</InputLabel>
              <Select
                labelId='state-label'
                name='state'
                onChange={(e) => {
                  setStateUS(e.target.value);
                }}
                value={stateUS}
                MenuProps={{
                  MenuListProps: { disablePadding: true },
                  slotProps: {
                    paper: { sx: { minWidth: 'unset !important' } },
                  },
                }}
                sx={{
                  backgroundColor: 'unset',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}>
                <MenuItem value=''></MenuItem>
                {states.map((state) => {
                  return (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </ListItem>
        </List></form>
      <Card raised sx={{ maxWidth: 1200, width: '100%', px: 1 }} style={{ marginBottom: "50px" }}>
        {tableData.length === 0 && <>No data for selection</>}
        {tableData.length > 0 && <BaseTable
          minWidth={1000}
          rows={tableData}
          columns={columns}
          tableColumnExtensions={tableColumnExtensions}
        >


          <EditingState />
          <FilteringState />
          <SortingState
            defaultSorting={[
              { columnName: 'name', direction: 'asc' },
            ]}
          />
          <PagingState defaultCurrentPage={0} pageSize={10} />

          <IntegratedFiltering
            columnExtensions={integratedFilteringColumnExtensions}
          />
          <IntegratedSorting
            columnExtensions={integratedSortingColumnExtensions}
          />
          <IntegratedPaging />
        </BaseTable>}
      </Card>
    </div>
  </div>
}

function BenchmarkInfo(props) {
  const { benchmarkData } = props;

  const [percentile, setPercentile] = useState(50);

  return <div>

    <div>Mean: {benchmarkData.mean.toFixed(2)}</div>
    <div>Median: {benchmarkData.median.toFixed(2)}</div>
    <br></br>
    <div>Count: {benchmarkData.quality_statistics.count}</div>
    <div>Min: {benchmarkData.quality_statistics.min.toFixed(2)}</div>
    <div>Max: {benchmarkData.quality_statistics.max.toFixed(2)}</div>
    <div>Range: {benchmarkData.quality_statistics.range.toFixed(2)}</div>
    <div>Standard Deviation: {benchmarkData.quality_statistics.standard_deviation.toFixed(2)}</div>
    <br></br>
    <List disablePadding>
      <ListItem disableGutters>
        <FormControl id='percentile' variant='standard' fullWidth>
          <InputLabel id='percentile-label'>Percentile</InputLabel>
          <Select
            labelId='percentile-label'
            name='percentile'
            onChange={(e) => {
              setPercentile(e.target.value);
            }}
            value={percentile}
            MenuProps={{
              MenuListProps: { disablePadding: true },
              slotProps: {
                paper: { sx: { minWidth: 'unset !important' } },
              },
            }}
            sx={{
              backgroundColor: 'unset',
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
              },
            }}>
            <MenuItem value=''></MenuItem>
            {Array.from({ length: 101 }, (x, i) => i).map((perc) => {
              return (
                <MenuItem key={perc} value={perc}>
                  {perc}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </ListItem>
    </List>
    <div>Percentile: {benchmarkData.percentiles[percentile].toFixed(2)}</div>

  </div>
}